import React from 'react';
import { Helmet } from 'react-helmet';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';
import SchemaOrg from '../components/SchemaOrg';
import CallbackForm from '../components/CallbackForm';
import { track } from '../lib/track';

const KontaktPage = () => {
  const phone = '+4923139085116';
  const displayPhone = '0231 39085116';
  const email = 'info@schluesseldienst.services';
  const whatsapp = `https://wa.me/4923139085116?text=Hallo, ich benötige Hilfe beim Schlüsseldienst`;

  return (
    <>
      <Helmet>
        <title>Kontakt – Schlüsseldienst Dortmund | 24/7 Notdienst</title>
        <meta name="description" content="Kontaktieren Sie uns 24/7 unter 0231 39085116. Schnelle Türöffnung in ganz Dortmund. Festpreise, keine versteckten Kosten." />
        <link rel="canonical" href="https://schluesseldienst.services/kontakt" />
      </Helmet>
      <SchemaOrg type="contact" />
      
      <div className="container mx-auto px-4 py-12">
        <Breadcrumbs items={[{ label: 'Kontakt' }]} />
        
        <h1 className="text-4xl sm:text-5xl font-bold mb-8">Kontakt</h1>
        
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl shadow-xl p-8 mb-12 border-2 border-orange-200">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-4">24/7 Notdienst – Wir sind immer für Sie da!</h2>
              <p className="text-lg text-gray-700">
                Ausgesperrt? Schlüssel verloren? Rufen Sie uns jetzt an!
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4 mb-6">
              <a
                href={`tel:${phone}`}
                onClick={() => track('call_button_click', { location: 'kontakt' })}
                className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-full font-bold text-xl transition-all shadow-lg hover:shadow-xl flex items-center gap-3"
                data-testid="kontakt-call-button"
              >
                <Phone size={24} />
                {displayPhone}
              </a>
              <a
                href={whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track('whatsapp_click', { location: 'kontakt' })}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-full font-bold text-xl transition-all shadow-lg hover:shadow-xl flex items-center gap-3"
                data-testid="kontakt-whatsapp-button"
              >
                <Phone size={24} />
                WhatsApp
              </a>
            </div>

            <p className="text-center text-gray-600">
              Montag bis Sonntag • 24 Stunden erreichbar • Auch an Feiertagen
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Telefon */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-orange-100 p-3 rounded-full">
                  <Phone className="text-orange-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold">Telefon</h3>
              </div>
              <a 
                href={`tel:${phone}`}
                className="text-2xl font-bold text-orange-600 hover:text-orange-700 block mb-2"
              >
                {displayPhone}
              </a>
              <p className="text-gray-600">
                Rufen Sie uns direkt an – wir nehmen Ihren Notruf entgegen und sind schnell bei Ihnen.
              </p>
            </div>

            {/* E-Mail */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-orange-100 p-3 rounded-full">
                  <Mail className="text-orange-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold">E-Mail</h3>
              </div>
              <a 
                href={`mailto:${email}`}
                className="text-lg font-semibold text-orange-600 hover:text-orange-700 block mb-2"
              >
                {email}
              </a>
              <p className="text-gray-600">
                Für allgemeine Anfragen, Angebote oder Terminvereinbarungen senden Sie uns eine E-Mail.
              </p>
            </div>

            {/* Adresse */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-orange-100 p-3 rounded-full">
                  <MapPin className="text-orange-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold">Adresse</h3>
              </div>
              <address className="text-gray-700 not-italic">
                <strong>Schössow Dienstleistungen UG</strong><br />
                (haftungsbeschränkt)<br />
                Neben dem Brand 10<br />
                44135 Dortmund
              </address>
              <p className="text-gray-600 mt-3">
                Unsere Zentrale liegt zentral in Dortmund für schnelle Anfahrt in alle Stadtteile.
              </p>
            </div>

            {/* Öffnungszeiten */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-orange-100 p-3 rounded-full">
                  <Clock className="text-orange-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold">Verfügbarkeit</h3>
              </div>
              <div className="space-y-2 text-gray-700">
                <div className="flex justify-between">
                  <span className="font-semibold">Notdienst:</span>
                  <span>24/7</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold">Montag - Freitag:</span>
                  <span>00:00 - 24:00</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold">Samstag - Sonntag:</span>
                  <span>00:00 - 24:00</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold">Feiertage:</span>
                  <span>00:00 - 24:00</span>
                </div>
              </div>
              <p className="text-gray-600 mt-3 text-sm">
                Wir sind rund um die Uhr für Notfälle erreichbar!
              </p>
            </div>
          </div>

          {/* Kontaktformular Alternative - Info */}
          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-lg mb-12">
            <h3 className="text-xl font-bold mb-3 text-gray-900">Schnelle Hilfe im Notfall</h3>
            <p className="text-gray-700 mb-4">
              Bei einem Notfall (ausgesperrt, Schlüssel verloren, etc.) rufen Sie uns bitte direkt an. 
              So können wir Ihnen am schnellsten helfen und sind in der Regel innerhalb von 15-30 Minuten bei Ihnen.
            </p>
            <p className="text-gray-700">
              <strong>Wichtig:</strong> Am Telefon erhalten Sie sofort einen verbindlichen Festpreis – 
              keine versteckten Kosten, keine Überraschungen!
            </p>
          </div>

          {/* Rückruf-Formular */}
          <div className="mb-12">
            <CallbackForm />
          </div>

          {/* Einsatzgebiete Hinweis */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
            <h2 className="text-2xl font-bold mb-4">Wir sind in ganz Dortmund für Sie da</h2>
            <p className="text-gray-700 mb-4">
              Unser Schlüsseldienst ist in allen 52 Stadtteilen von Dortmund aktiv. 
              Egal ob Innenstadt, Hörde, Hombruch, Aplerbeck oder ein anderer Stadtteil – 
              wir erreichen Sie schnell und zuverlässig.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="/einsatzgebiete" className="inline-block bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-full font-semibold transition-all">
                Alle Einsatzgebiete ansehen
              </a>
              <a href="/preise" className="inline-block bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-full font-semibold transition-all">
                Preise & Leistungen
              </a>
            </div>
          </div>

          {/* FAQs */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-6">Häufige Fragen</h2>
            
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-bold text-lg mb-2">Wie schnell sind Sie vor Ort?</h3>
              <p className="text-gray-700">
                In der Regel sind wir innerhalb von 15-30 Minuten bei Ihnen in Dortmund. 
                Die genaue Zeit hängt von Ihrem Standort und der aktuellen Verkehrslage ab.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-bold text-lg mb-2">Was kostet eine Türöffnung?</h3>
              <p className="text-gray-700">
                Unsere Festpreise: Mo-Fr 08-20 Uhr: 119 €, Sa-So: 199 €. 
                Die Anfahrt ist bereits im Preis enthalten. Sie erhalten den genauen Preis bereits am Telefon.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-bold text-lg mb-2">Welche Zahlungsarten akzeptieren Sie?</h3>
              <p className="text-gray-700">
                Wir akzeptieren Barzahlung, EC-Karte und alle gängigen Kreditkarten (Visa, Mastercard, etc.).
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-bold text-lg mb-2">Wird die Tür beschädigt?</h3>
              <p className="text-gray-700">
                Nein, wir verwenden professionelle Werkzeuge und schonende Öffnungstechniken. 
                In über 95% der Fälle öffnen wir ohne jegliche Beschädigung von Tür oder Schloss.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default KontaktPage;